function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { forwardRef, useCallback, useContext, useEffect, useRef, useMemo } from 'react';
import { PdfHighlighter, Highlight, AreaHighlight } from 'react-pdf-highlighter';
import cx from 'classnames';
import { PDFFindController } from 'pdfjs-dist/web/pdf_viewer';
import EditorPdfLoader from './EditorPdfLoader';
import EditorContext from '../store/Editor.context';
import { setMounted, addChanges, updateChanges, setDocument, updateStore } from '../store';
import { clearSelection, makeThumb } from '../utils/helpers';
import { EDITOR_TOOLS, EDITOR_STEPS } from '../constants';
import Loader from '../components/shared/Loader';
var timeoutScrolling;
var lastPage; // eslint-disable-next-line react/display-name

var EditorViewer = forwardRef(function (_ref, ref) {
  var _state$uploadedPdfDoc, _state$uploadedPdfDoc2;

  var thumbnailref = _ref.thumbnailref;

  var _useContext = useContext(EditorContext),
      state = _useContext.state,
      dispatch = _useContext.dispatch;

  var shouldStartFreeform = useRef(false);
  var isActiveFreeform = useRef(false);
  var isActiveText = useRef(false);
  var isActiveEdit = useRef(false);
  var isActiveColor = useRef(false);

  if ((_state$uploadedPdfDoc = state.uploadedPdfDocument) === null || _state$uploadedPdfDoc === void 0 ? void 0 : _state$uploadedPdfDoc.promise) {
    state.uploadedPdfDocument.promise.then(function (pdf) {
      dispatch(updateStore({
        uploadedPdfDocument: pdf
      }));
    });
  }

  var pdfDocument = state.pdfDocument || ((_state$uploadedPdfDoc2 = state.uploadedPdfDocument) === null || _state$uploadedPdfDoc2 === void 0 ? void 0 : _state$uploadedPdfDoc2.fingerprint) && state.uploadedPdfDocument;
  isActiveFreeform.current = state.activeTool === EDITOR_TOOLS.freeform;
  isActiveText.current = state.activeTool === EDITOR_TOOLS.text;
  isActiveEdit.current = state.activeStep === EDITOR_STEPS.edit;
  isActiveColor.current = state.activeColor;
  useEffect(function () {
    shouldStartFreeform.current = isActiveFreeform.current; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveFreeform.current]);

  var onScroll = function onScroll() {
    var currentPage = window.PdfViewer.viewer.currentPageNumber - 1;
    if (currentPage === lastPage) return;
    lastPage = currentPage;
    var thumb = document.getElementById(currentPage);
    var activeThumbs = document.querySelectorAll('#thumbnailPanel canvas.border-positive');

    if (activeThumbs === null || activeThumbs === void 0 ? void 0 : activeThumbs.length) {
      activeThumbs.forEach(function (item) {
        item.classList.remove('border-positive');
      });
    }

    if (thumb) {
      thumb.classList.add('border-positive');
      thumb.scrollIntoView({
        block: 'nearest'
      });
    }
  };

  var handleScroll = useCallback(function () {
    onScroll();
    window.clearTimeout(timeoutScrolling);
    timeoutScrolling = setTimeout(function () {
      onScroll();
    }, 66);
  }, []);

  var handleRemoveThumbnail = function handleRemoveThumbnail() {
    while (thumbnailref.current.firstChild) {
      thumbnailref.current.removeChild(thumbnailref.current.lastChild);
    }
  };

  useEffect(function () {
    if (!state.mounted && state.processing) {
      handleRemoveThumbnail();
    }
  }, [state.mounted, state.processing]);
  var handleSetRef = useCallback(function (innerRef) {
    ref.current = innerRef;

    if (!state.mounted && pdfDocument && (innerRef === null || innerRef === void 0 ? void 0 : innerRef.viewer.pdfDocument)) {
      var pdfRef = ref.current;

      var selectionEndListener = function selectionEndListener() {
        return pdfRef.afterSelection();
      };

      pdfRef.debouncedAfterSelection = function () {
        window.addEventListener('mouseup', selectionEndListener, {
          once: true
        });
      };

      pdfRef.toggleTextSelection = function () {};

      var findController = new PDFFindController({
        linkService: pdfRef.viewer.linkService,
        eventBus: pdfRef.eventBus
      });
      pdfRef.viewer.findController = findController;
      pdfRef.findController = findController;
      window.PdfViewer.viewer.container.addEventListener('scroll', handleScroll);

      var handleMakeThumbnail = function handleMakeThumbnail() {
        handleRemoveThumbnail();
        var pdfDocument = pdfRef.viewer.pdfDocument;
        var pages = new Array(pdfDocument.numPages).fill(null);
        pages.forEach(function (_page, index) {
          var pageNumber = index + 1;
          var div = document.createElement('div');
          thumbnailref.current.appendChild(div);
          pdfDocument.getPage(pageNumber).then(function (page) {
            return makeThumb(page, function () {
              return pdfRef.viewer.scrollPageIntoView({
                pageNumber: pageNumber
              });
            });
          }).then(function (canvas) {
            div.appendChild(canvas);
          });
        });
      };

      handleMakeThumbnail();
      setTimeout(function () {
        dispatch(setMounted);
      }, 0);
    }
  }, [state.mounted, pdfDocument]);

  var handleSelection = function handleSelection(position, content, hideTipAndSelection) {
    if (isActiveText.current || shouldStartFreeform.current) {
      var viewport = ref.current.viewer.getPageView(ref.current.viewer.currentPageNumber - 1).viewport;

      var newPosition = _extends({}, position, {
        pageSize: {
          width: viewport === null || viewport === void 0 ? void 0 : viewport.viewBox[2],
          height: viewport === null || viewport === void 0 ? void 0 : viewport.viewBox[3],
          scale: viewport === null || viewport === void 0 ? void 0 : viewport.scale
        }
      });

      dispatch(addChanges({
        content: _extends({}, content, {
          color: isActiveColor.current
        }),
        position: newPosition
      }));

      if (shouldStartFreeform.current) {
        var el = document.querySelector('.MouseSelection');
        if (el) el.classList.add('hidden');
      } else {
        clearSelection();
      }

      hideTipAndSelection();
    }
  };

  var file = useMemo(function () {
    if (state.activeStep === EDITOR_STEPS.edit) {
      return !state.uploadedPdfDocument && state.uploadedFile;
    } else {
      return state.editedUrl;
    }
  }, [state.activeStep, state.uploadedFile, state.editedUrl, state.processing]);
  var preloader = /*#__PURE__*/React.createElement("div", {
    className: "w-full h-full flex items-center justify-center"
  }, /*#__PURE__*/React.createElement(Loader, null, state.processing || 'Loading...'));
  return /*#__PURE__*/React.createElement("div", {
    id: "pdf-viewer",
    className: cx(['flex-1 min-w-0', {
      freeform: isActiveFreeform.current
    }])
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: '100%',
      width: '100%',
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(EditorPdfLoader, {
    file: file,
    loading: preloader,
    onLoadSuccess: function onLoadSuccess(pdf) {
      dispatch(setDocument(pdf));

      if (!state.uploadedPdfDocument) {
        dispatch(updateStore({
          uploadedPdfDocument: pdf
        }));
      }
    }
  }, pdfDocument && !state.processing ? /*#__PURE__*/React.createElement(PdfHighlighter, {
    ref: handleSetRef,
    pdfDocument: pdfDocument,
    enableAreaSelection: function enableAreaSelection() {
      return shouldStartFreeform.current;
    },
    scrollRef: function scrollRef() {},
    onSelectionFinished: handleSelection,
    highlightTransform: function highlightTransform(highlight, _index, _setTip, _hideTip, viewportToScaled, screenshot, isScrolledTo) {
      var isTextHighlight = !(highlight.content && highlight.content.image); // eslint-disable-next-line no-nested-ternary

      var highlightComponent = isTextHighlight ? /*#__PURE__*/React.createElement("div", {
        ref: function ref(_ref2) {
          setTimeout(function () {
            var _ref$children;

            if (_ref2 === null || _ref2 === void 0 ? void 0 : (_ref$children = _ref2.children) === null || _ref$children === void 0 ? void 0 : _ref$children[0]) {
              var _ref$children2;

              var parts = _ref2 === null || _ref2 === void 0 ? void 0 : (_ref$children2 = _ref2.children) === null || _ref$children2 === void 0 ? void 0 : _ref$children2[0].querySelectorAll('.Highlight__part');
              parts.forEach(function (part) {
                return part.style.backgroundColor = highlight.content.color;
              });
            }
          }, 0);
        }
      }, /*#__PURE__*/React.createElement(Highlight, {
        key: highlight.id,
        position: highlight.position,
        isScrolledTo: isScrolledTo,
        style: {
          background: highlight.content.color
        }
      })) : /*#__PURE__*/React.createElement("div", {
        key: highlight.id,
        onMouseEnter: function onMouseEnter() {
          if (isActiveFreeform.current) {
            shouldStartFreeform.current = false;
          }
        },
        onMouseLeave: function onMouseLeave() {
          if (isActiveFreeform.current) {
            shouldStartFreeform.current = true;
          }
        },
        ref: function ref(_ref3) {
          setTimeout(function () {
            var _ref$children3;

            if (_ref3 === null || _ref3 === void 0 ? void 0 : (_ref$children3 = _ref3.children) === null || _ref$children3 === void 0 ? void 0 : _ref$children3[0]) {
              _ref3.children[0].style.backgroundColor = highlight.content.color;
            }
          }, 0);
        }
      }, /*#__PURE__*/React.createElement(AreaHighlight, {
        highlight: highlight,
        enableResizing: false,
        disableDragging: !isActiveFreeform.current,
        onChange: function onChange(boundingRect) {
          dispatch(updateChanges(highlight.id, {
            position: {
              boundingRect: viewportToScaled(boundingRect)
            },
            content: {
              image: screenshot(boundingRect)
            }
          }));
        },
        onDragStart: function onDragStart(e) {
          e.stopPropagation();
          e.preventDefault();
          shouldStartFreeform.current = false;
        }
      }));
      return highlightComponent;
    },
    highlights: isActiveEdit.current ? state.changes.present : []
  }) : preloader)));
});
export default EditorViewer;